'use client';

import Image from '@/components/Image';
import {
  addSequentialAnimations,
  handleAnimationError,
  useInViewportAnimation,
} from '@/hooks/useInViewportAnimation';
import '@/styles/animations.css';
import React from 'react'; // Added missing import
import { Swiper, SwiperSlide } from 'swiper/react';

interface IntroItem {
  image?: string;
  title?: string;
  description?: string;
}

interface FunctionItem {
  image?: string;
  word?: string;
  title?: string;
}

interface LensIntroItem {
  image?: string;
  title?: string;
  description?: string;
}

interface StepItem {
  word1?: string;
  word2?: string;
}

interface DrivingIntroData {
  title?: string;
  intro_arr?: IntroItem[];
}

interface DrivingBenefitsData {
  title?: string;
  function_intro?: FunctionItem[];
  lens_introduction?: LensIntroItem[];
}

interface CustomizeGlassesData {
  title?: string;
  step_arr?: StepItem[];
}

interface DrivingBenefitProps {
  drivingData: {
    driving_intro?: DrivingIntroData;
    driving_benefits?: DrivingBenefitsData;
    customize_glasses?: CustomizeGlassesData;
  };
  isMobile: boolean;
  isFromCache?: boolean;
  onExposure?: (section: string) => void;
}

export default function DrivingBenefit({
  drivingData,
  isMobile,
  isFromCache = false,
  onExposure,
}: DrivingBenefitProps) {
  const drivingIntro = drivingData.driving_intro || {};
  const drivingBenefits = drivingData.driving_benefits || {};

  // 如果从缓存恢复，确保元素可见
  React.useEffect(() => {
    if (isFromCache || isMobile) {
      setTimeout(() => {
        // 移除动画相关的opacity设置，确保元素可见
        const animationElements = [
          'driving-benefit-intro-title',
          'driving-benefit-intro-li',
          'driving-benefit-benefit-title',
          'driving-benefit-box-function-li',
        ];

        animationElements.forEach((className) => {
          const elements = document.getElementsByClassName(className);
          Array.from(elements).forEach((el) => {
            const htmlEl = el as HTMLElement;
            htmlEl.style.opacity = '1';
            htmlEl.style.transform = 'translateY(0)';
            // 移除可能存在的动画类
            htmlEl.classList.remove('fadeInDownAnimate', 'fadeInUpAnimate', 'opacity0');
          });
        });
      }, 100);
    }
  }, [isFromCache, isMobile]);

  // 驾驶优势1动画触发器 - 只在PC端生效
  const introAnimationRef = useInViewportAnimation(
    () => {
      try {
        onExposure?.('驾驶镜优势1');

        // 如果从缓存恢复或移动端，跳过动画
        if (isFromCache || isMobile) return;

        // 标题从上淡入
        const titleElement = document.getElementsByClassName(
          'driving-benefit-box-intro-title',
        )?.[0];
        if (titleElement) {
          titleElement.classList.add('fadeInDownAnimate');
        }

        // 列表项依次从下淡入 - 对应Vue中的延迟动画
        addSequentialAnimations('driving-benefit-box-intro-li', 'fadeInUpAnimate', 100);
      } catch {
        handleAnimationError();
      }
    },
    { threshold: 0.4 },
  );

  // 驾驶优势2动画触发器 - 只在PC端生效
  const benefitAnimationRef = useInViewportAnimation(
    () => {
      try {
        onExposure?.('驾驶镜优势2');

        // 如果从缓存恢复或移动端，跳过动画
        if (isFromCache || isMobile) return;

        // 标题从上淡入
        const titleElement = document.getElementsByClassName('driving-benefit-benefit-title')?.[0];
        if (titleElement) {
          titleElement.classList.add('fadeInDownAnimate');
        }

        // 功能介绍项依次从下淡入
        addSequentialAnimations('driving-benefit-box-function-li', 'fadeInUpAnimate', 100);

        // 镜片介绍项依次从下淡入
        addSequentialAnimations('driving-benefit-box-benefit-li', 'fadeInUpAnimate', 100);
      } catch {
        handleAnimationError();
      }
    },
    { threshold: 0.4 },
  );

  return (
    <div>
      {/* Driving Benefit Box */}
      <div
        className={`driving-benefit-box ${
          isMobile ? 'pt-[50px] px-0 pb-[40px]' : 'py-0 px-[80px] xl:px-[100px] 2xl:px-[120px]'
        }`}
      >
        {/* Intro Box */}
        <div
          ref={introAnimationRef as React.RefObject<HTMLDivElement>}
          className={`intro-box driving-benefit-intro-box ${
            isMobile ? 'px-[16px] pb-[30px]' : 'mb-[80px] px-0 pb-0 xl:mb-[100px] 2xl:mb-[120px]'
          }`}
          onClick={() => onExposure?.('驾驶镜优势1')}
        >
          {/* 标题 */}
          <h3
            className={`title ${!isMobile ? 'driving-benefit-box-intro-title opacity0' : ''} text-white ${
              isMobile
                ? 'text-left text-[20px] leading-[30px] font-bs mb-6'
                : 'text-center font-bb mb-[56px] text-[48px] xl:text-[48px] xl:mb-[56px] 2xl:text-[48px] 2xl:mb-[56px]'
            }`}
          >
            {drivingIntro.title}
          </h3>

          {/* 列表 */}
          <ul
            className={`list-none p-0 m-0 ${
              isMobile ? '' : 'flex justify-between items-center gap-x-[24px]'
            }`}
          >
            {drivingIntro.intro_arr?.map((item, index) => (
              <li
                key={index}
                className={`${!isMobile ? 'driving-benefit-box-intro-li opacity0' : ''} ${
                  isMobile ? 'block' : 'relative'
                }`}
              >
                {isMobile ? (
                  // 移动端布局
                  <>
                    <div className="w-full">
                      <Image
                        src={item.image || ''}
                        alt={item.title || ''}
                        width={343}
                        height={193}
                        className="w-full h-auto block"
                      />
                    </div>
                    <div className="w-full text-white box-border pt-[15px] pb-[40px]">
                      <h4 className="font-bs text-[14px] mb-[10px]">{item.title}</h4>
                      <p className="m-0 text-[10px] leading-[15px]">{item.description}</p>
                    </div>
                  </>
                ) : (
                  // PC端布局 - 图片+overlay
                  <>
                    <Image
                      src={item.image || ''}
                      alt={item.title || ''}
                      width={887}
                      height={486}
                      className="block !w-[full] !h-auto"
                    />
                    <div
                      className="absolute bottom-[8px] left-[0] w-[full] h-[90px] mx-[12px] xl:bottom-[11px] xl:left-[0] xl:h-[110px] 2xl:bottom-[12px] 2xl:left-[0] 2xl:h-[120px] rounded-[12px] xl:rounded-[15px] 2xl:rounded-[16px] text-white px-[18px] xl:px-[22px] 2xl:px-[24px] box-border flex flex-col justify-center items-start"
                      style={{
                        background: 'rgba(255,255,255,0.08)',
                        backdropFilter: 'blur(8px)',
                        width: 'calc(100% - 24px)',
                      }}
                    >
                      <h4 className="font-bs text-white mb-[6px] text-[18px] xl:text-[22px] 2xl:text-[24px] xl:mb-[7px] 2xl:mb-[8px]">
                        {item.title}
                      </h4>
                      <p className="m-0 text-white text-[12px] xl:text-[13px] 2xl:text-[14px] leading-[20px] xl:leading-[24px] 2xl:leading-[26px]">
                        {item.description}
                      </p>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Benefit Box */}
        <div
          ref={benefitAnimationRef as React.RefObject<HTMLDivElement>}
          className={`benefit-box driving-benefit-benefit-box ${isMobile ? 'px-[16px]' : ''}`}
          onClick={() => onExposure?.('驾驶镜优势2')}
        >
          {/* 标题 */}
          <h2
            className={`title ${!isMobile ? 'driving-benefit-benefit-title opacity0' : ''} text-white ${
              isMobile
                ? 'text-left text-[20px] leading-[30px] font-bs mb-[24px]'
                : 'text-center font-bb mb-[56px] text-[36px] xl:text-[45px] 2xl:text-[48px] xl:mb-[50px] 2xl:mb-[56px]'
            }`}
          >
            {drivingBenefits.title}
          </h2>

          {/* Function Box */}
          {isMobile ? (
            // 移动端网格布局
            <ul className="function-box list-none m-0 bg-[#454557] rounded-xl grid grid-cols-2 gap-y-[30px] gap-x-[10px] pt-[20px] pb-[20px] px-[14px]">
              {drivingBenefits.function_intro?.map((item, index) => (
                <li
                  key={index}
                  className={`${!isMobile ? 'driving-benefit-box-function-li opacity0' : ''} flex items-center justify-center`}
                >
                  <Image
                    src={item.image || ''}
                    alt={item.title || item.word || ''}
                    width={32}
                    height={32}
                    className="w-[32px] h-auto mr-[10px]"
                  />
                  <h5 className="text-[11px] text-white leading-[16px] m-0">{item.word}</h5>
                </li>
              ))}
            </ul>
          ) : (
            // PC端横向布局
            <ul className="function-box list-none mb-[36px] xl:mb-[43px] 2xl:mb-[46px] grid grid-cols-4 gap-x-[20px]">
              {drivingBenefits.function_intro?.map((item, index) => (
                <li
                  key={index}
                  className={`${!isMobile ? 'driving-benefit-box-function-li opacity0' : ''} flex items-center justify-center bg-[#24242c] rounded-lg w-[full] h-[66px] xl:h-[81px] 2xl:h-[88px] px-[12px] xl:px-[14px] 2xl:px-[16px]`}
                >
                  <Image
                    src={item.image || ''}
                    alt={item.title || item.word || ''}
                    width={48}
                    height={48}
                    className="h-auto w-[36px] mr-[18px] lg:w-[40px] lg:mr-[20px] xl:w-[44px] xl:mr-[22px] 2xl:w-[48px] 2xl:mr-[24px]"
                  />
                  <h5 className="text-white m-0 text-[12px] lg:text-[14px] xl:text-[15px] 2xl:text-[16px]">
                    {item.word}
                  </h5>
                </li>
              ))}
            </ul>
          )}
        </div>
        {/* Lens Introduction */}
        {isMobile ? (
          // 移动端使用Swiper
          <Swiper
            className="benefit-intro-box mt-[8px]"
            slidesPerView={1.4}
            spaceBetween={8}
            slidesOffsetBefore={16}
            slidesOffsetAfter={16}
          >
            {drivingBenefits.lens_introduction?.map((item, index) => (
              <SwiperSlide
                key={index}
                className="benefit-slide rounded-xl"
                style={{
                  background: 'linear-gradient(336deg, #19191E 0%, #5A5B74 100%)',
                  boxShadow: '0px 1px 2px 0px rgba(0,0,0,0.5)',
                }}
              >
                <Image
                  src={item.image || ''}
                  alt={item.title || ''}
                  width={240}
                  height={135}
                  className="w-full h-auto block rounded-t-xl"
                  style={{ marginBottom: '10px' }}
                />
                <div className="word-box text-white h-[142px] pt-[10px] px-[12px] pb-0">
                  <h3 className="text-[16px] font-bs text-white mb-[10px]">{item.title}</h3>
                  <p className="text-[11px] leading-[16px] text-white m-0">{item.description}</p>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          // PC端横向布局
          <ul className="benefit-intro-box list-none p-0 m-0 flex justify-between items-center grid grid-cols-3 gap-x-[20px] pb-[40px] xl:pb-[60px] 2xl:pb-[80px]">
            {drivingBenefits.lens_introduction?.map((item, index) => (
              <li
                key={index}
                className={`${!isMobile ? 'driving-benefit-box-benefit-li opacity0' : ''} rounded-[15px] xl:rounded-[18px] 2xl:rounded-[20px]`}
                style={{
                  background: 'linear-gradient(336deg, #19191E 0%, #5A5B74 100%)',
                  boxShadow: '0px 2px 4px 0px rgba(0,0,0,0.5)',
                }}
              >
                <Image
                  src={item.image || ''}
                  alt={item.title || ''}
                  width={550}
                  height={309}
                  className="w-full h-auto block mb-[16px] xl:mb-[20px] 2xl:mb-[24px]"
                />
                <div className="word-box text-white px-[18px] pb-0 h-[150px] xl:h-[170px] xl:px-[22px] 2xl:h-[182px] 2xl:px-[24px]">
                  <h3 className="font-bs text-white text-[1.56vw] mb-[12px] xl:mb-[15px] 2xl:mb-[16px]">
                    {item.title}
                  </h3>
                  <p className="text-white m-0 text-[10px] leading-[18px] xl:text-[13px] xl:leading-[23px] 2xl:text-[14px] 2xl:leading-[26px]">
                    {item.description}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
